import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { API_URL } from '../config/constants'
import { calcApi } from './calc'
import { getAcceptLanguage } from '../config/i18'
import { rootReducer } from '../store'
import { getRequestLanguage } from './auth'
import {DEMO_CALC_ID} from "../utils/hooks";

const getTestDriveTotalAmountExchange = (order: TestDriveOrder): TotalAmountExchange => ({
  [order.base_currency]: {
    net_value: order.summaryOrder.total_payment_amount,
    value: order.summaryOrder.total_amount,
  },
  ...order.summaryOrder.total_amount_exchange,
})

export const getTestDriveOrderSummaryResponse = (
  order: TestDriveOrder,
): TestDriveOrderSummaryResponse => ({
  id: order.id,
  customs: order.summaryCustoms,
  packaging: order.summaryOrder,
  delivery: order.route,
  completed: order.summaryCompleted,
  summary: {
    total_amount: order.summaryOrder.total_amount,
    total_amount_exchange: getTestDriveTotalAmountExchange(order),
    total_banking_commissions: order.summaryOrder.total_banking_commissions,
    total_currency_control_fees: order.summaryOrder.total_currency_control_fees,
    total_payment_amount: order.summaryOrder.total_payment_amount,
    total_payment_amount_with_commissions: order.summaryOrder.total_payment_amount_with_commissions,
  },
})

export const hydrateTestDriveOrder = async ({ dispatch, order, shouldReset = true }: HydrateTestDriveOrderParams) => {
  const calculationOrder = order as unknown as OrderParamsOutput
  const invoices = order.invoices as unknown as InvoiceParamsOutput[]
  const upsert = (endpointName: string, arg: any, data: any) =>
    dispatch(calcApi.util.upsertQueryData(endpointName as any, arg, data))

  if (shouldReset) {
    dispatch(calcApi.util.resetApiState())
  }

  await Promise.all([
    upsert('orderById', DEMO_CALC_ID, calculationOrder),
    upsert('getOrderSummaryById', DEMO_CALC_ID, getTestDriveOrderSummaryResponse(order)),
    upsert('invoicesByOrderId', DEMO_CALC_ID, invoices),
    upsert('summaryByOrderId', DEMO_CALC_ID, order.summaryOrder),
    upsert('declarationsByOrderId', DEMO_CALC_ID, order.declarations),
    upsert('declarationSummaryByOrderId', DEMO_CALC_ID, order.summaryCustoms),
    upsert('getRouteByOrderId', DEMO_CALC_ID, order.route),
    upsert('getOrderSummaryInvoicesById', DEMO_CALC_ID, order.summaryInvoices),
    upsert('getBankCommissionsByOrderId', { orderId: DEMO_CALC_ID }, order.bankCommissions),
    upsert('getAdditionalServicesByOrderId', DEMO_CALC_ID, order.additionalServices || []),
    upsert('getExtraServicesByOrderId', DEMO_CALC_ID, order.userExtraServices || []),
    upsert('getUserBrokerServicesByOrderId', DEMO_CALC_ID, null),
    upsert('getBrokerDataByCode', DEMO_CALC_ID, order.cert_codes),
    ...invoices.map(invoice => upsert('productsByInvoiceId', invoice.id, invoice.products || [])),
  ])
}

export const testDriveApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: async (headers, { getState }) => {
      const user = (getState() as ReturnType<typeof rootReducer>)?.userParams?.me
      const requestLanguage = await getRequestLanguage(user)

      headers.set('Accept-Language', getAcceptLanguage(requestLanguage))
      return headers
    },
  }),
  endpoints: (builder) => ({
    getTestDriveScenario: builder.query<TestDriveScenarioDetails, string>({
      query: (slug) => `test-drive/v1/scenarios/${slug}/`,
    }),
    getTestDriveScenarios: builder.query<TestDriveScenario[], void>({
      query: () => 'test-drive/v1/scenarios/',
    }),
    recalculateTestDriveScenario: builder.mutation<TestDriveScenarioDetails, TestDriveScenarioRecalculateArgs>({
      query: ({ slug, ...body }) => ({
        body,
        method: 'POST',
        url: `test-drive/v1/scenarios/${slug}/recalculate/`,
      }),
      async onQueryStarted({ slug }, { dispatch, queryFulfilled }) {
        try {
          const { data: scenarioDetails } = await queryFulfilled

          dispatch(testDriveApi.util.upsertQueryData('getTestDriveScenario', slug, scenarioDetails))
        } catch {
          // The mutation result exposes the request error to the caller.
        }
      },
    }),
  }),
  reducerPath: 'testDriveApi',
})

export const {
  useGetTestDriveScenarioQuery,
  useGetTestDriveScenariosQuery,
  useLazyGetTestDriveScenarioQuery,
  useLazyGetTestDriveScenariosQuery,
  useRecalculateTestDriveScenarioMutation,
} = testDriveApi
